import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../services/user.service.ts';
import { getOrCreateUniqueId, setUserIdAndNotify, formatError } from '../utils/index.ts';
import Loading from '../components/common/Loading.tsx';

const HomePage = () => {
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null as string | null);
  const navigate = useNavigate();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    await registerAndNavigate(name.trim() || null);
  };

  const handleSkip = async () => {
    await registerAndNavigate(null);
  };

  const registerAndNavigate = async (userName: string | null) => {
    setIsLoading(true);
    setError(null);

    try {
      const uniqueId = getOrCreateUniqueId();
      const user = await registerUser({
        unique_id: uniqueId,
        name: userName,
      });

      // Store user_id for future API calls and notify components of auth change
      setUserIdAndNotify(user.id);

      // Navigate to analysis page
      navigate('/analysis');
    } catch (err) {
      const errorMessage = formatError(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-black">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-black px-4 py-10 transition-colors duration-200">
      <div className="w-full max-w-md rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-sm">
        <div className="border-b border-slate-200 dark:border-zinc-700 px-6 py-6">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            Welcome to NLP IBM Debater
          </h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-zinc-400">
            Enter your name to get started, or skip to use a random name.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-slate-700 dark:text-zinc-300"
            >
              Your Name (Optional)
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              maxLength={100}
              className="mt-2 w-full rounded-md border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-4 py-2 text-sm text-slate-700 dark:text-white placeholder-slate-400 dark:placeholder-zinc-500 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-opacity-20"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 rounded-md bg-blue-600 dark:bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Continue
            </button>
            <button
              type="button"
              onClick={handleSkip}
              disabled={isLoading}
              className="flex-1 rounded-md border border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-zinc-200 transition hover:bg-slate-50 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-zinc-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Skip (Random Name)
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HomePage;

